import { useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { site } from '@shared/site.config';

interface Props {
  noi: number;
  capRate: number;
  indicatedValue: number | null;
}

function money(n: number) {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(n);
}

export default function PropertyValueLeadForm({ noi, capRate, indicatedValue }: Props) {
  const { pathname, search } = useLocation();
  const [form, setForm] = useState({
    address: '',
    propertyType: 'Industrial',
    timing: 'Just researching',
    name: '',
    email: '',
    phone: '',
    company: '',
    notes: '',
    _gotcha: '',
  });
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState('');

  const set =
    (key: keyof typeof form) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (state === 'sending') return;

    setState('sending');
    setError('');

    const calcSummary = indicatedValue
      ? `${money(noi)} annual NOI at ${capRate.toFixed(2)}% cap rate produced an indicative value of ${money(indicatedValue)}.`
      : `Calculator inputs: ${money(noi)} annual NOI and ${capRate.toFixed(2)}% cap rate.`;

    const message = [
      'Commercial property value review request',
      `Property address: ${form.address.trim()}`,
      `Property type: ${form.propertyType}`,
      `Owner timing: ${form.timing}`,
      calcSummary,
      form.notes.trim() ? `Owner notes: ${form.notes.trim()}` : '',
    ]
      .filter(Boolean)
      .join('\n');

    try {
      await api.submitLead({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        company: form.company.trim() || undefined,
        message,
        interest: 'buying-selling',
        sourcePath: pathname + search,
        _gotcha: form._gotcha || undefined,
      });
      setState('sent');
    } catch (err) {
      setState('error');
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  }

  if (state === 'sent') {
    return (
      <div className="sheet-panel sheet-marked shadow-sheet p-8 text-center">
        <p className="annot">Review requested</p>
        <h3 className="mt-2 font-display text-[26px]">Property details sent.</h3>
        <p className="mt-3 text-[14px] leading-relaxed text-annot">
          Your calculator assumptions and property details went directly to {site.agent.shortName}. The next step is a property-specific conversation, not an automated appraisal.
        </p>
        <a
          href={`tel:${site.agent.phone.replace(/\D/g, '')}`}
          className="mt-6 inline-block font-mono text-[10px] uppercase tracking-wider2 text-redline hover:text-redline-600"
        >
          Call {site.agent.phone}
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="sheet-panel shadow-sheet p-6 sm:p-7">
      <div className="mb-6 border-b border-hairline pb-5">
        <p className="annot">Owner follow-up</p>
        <h3 className="mt-2 font-display text-[26px] leading-tight">Request a property-specific value review</h3>
        <p className="mt-2 text-[14px] leading-relaxed text-annot">
          Send the property address and the calculator assumptions you just used. No obligation and no public listing is created.
        </p>
      </div>

      <div className="space-y-4">
        <label className="block">
          <span className="annot">Property address</span>
          <input
            required
            value={form.address}
            onChange={set('address')}
            className="field mt-1.5"
            autoComplete="street-address"
            placeholder="123 Example St, Cambridge, ON"
          />
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="annot">Property type</span>
            <select value={form.propertyType} onChange={set('propertyType')} className="field mt-1.5">
              <option>Industrial</option>
              <option>Retail</option>
              <option>Office</option>
              <option>Mixed-use</option>
              <option>Multi-residential 5+ units</option>
              <option>Land / redevelopment</option>
              <option>Other</option>
            </select>
          </label>

          <label className="block">
            <span className="annot">What best describes your timing?</span>
            <select value={form.timing} onChange={set('timing')} className="field mt-1.5">
              <option>Just researching</option>
              <option>Would sell if pricing is strong</option>
              <option>Considering a sale in 3–6 months</option>
              <option>Planning to sell within 90 days</option>
              <option>Need to sell / refinance soon</option>
            </select>
          </label>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="annot">Name</span>
            <input required value={form.name} onChange={set('name')} className="field mt-1.5" autoComplete="name" />
          </label>
          <label className="block">
            <span className="annot">Company — optional</span>
            <input value={form.company} onChange={set('company')} className="field mt-1.5" autoComplete="organization" />
          </label>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="annot">Email</span>
            <input required type="email" value={form.email} onChange={set('email')} className="field mt-1.5" autoComplete="email" />
          </label>
          <label className="block">
            <span className="annot">Phone — optional</span>
            <input value={form.phone} onChange={set('phone')} className="field mt-1.5" autoComplete="tel" />
          </label>
        </div>

        <label className="block">
          <span className="annot">Anything important about the property? — optional</span>
          <textarea
            rows={3}
            value={form.notes}
            onChange={set('notes')}
            className="field mt-1.5 resize-y"
            placeholder="Vacancy, lease expiry, redevelopment potential, recent improvements, sale timing…"
          />
        </label>

        <input
          type="text"
          name="_gotcha"
          value={form._gotcha}
          onChange={set('_gotcha')}
          tabIndex={-1}
          autoComplete="off"
          hidden
        />
      </div>

      <div className="mt-5 border-l-2 border-redline bg-redline-100/40 px-4 py-3">
        <p className="font-mono text-[10px] uppercase tracking-wider2 text-redline-600">Calculator snapshot attached</p>
        <p className="mt-1.5 text-[13px] leading-relaxed text-graphite-600">
          NOI {money(noi)} · Cap rate {capRate.toFixed(2)}%
          {indicatedValue ? ` · Indicated value ${money(indicatedValue)}` : ''}
        </p>
      </div>

      {state === 'error' && (
        <div className="mt-4 border-l-2 border-redline bg-redline-100/50 px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-wider2 text-redline-600">Not sent</p>
          <p className="mt-1.5 text-[13px] leading-relaxed text-graphite-600">
            {error} You can also call {site.agent.phone} directly.
          </p>
        </div>
      )}

      <button type="submit" disabled={state === 'sending'} className="btn-primary mt-6 w-full">
        {state === 'sending' ? 'Sending…' : 'Request property-specific review'}
      </button>

      <p className="mt-4 text-center text-[12px] leading-relaxed text-annot">
        Goes directly to {site.agent.name}. No newsletter, no public listing, no third-party lead resale.
      </p>
    </form>
  );
}
